import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchWallets,
  fetchWalletById,
  fetchWalletDetail,
  fetchTransferEstimate,
  createTransferDraft,
  submitTransferDraft,
  type TransferDestinationInput,
  type WalletListResult,
  type WalletLookupResult,
} from "@/lib/api";
import { walletDisplayName } from "@/lib/wallet-display-name";
import { readWalletBalances, type WalletBalanceStatus } from "@/lib/wallet-balances";
import type { TransferWalletOption, TransferAssetOption } from "@/types";

export interface WalletLookupFailure { reasonCode: string; supportReference?: string | null }

/** Only a code-shaped reason is kept for display and logging; free text from a body is dropped. */
function safeReasonCode(code: string): string {
  return /^[A-Z][A-Z0-9_]{1,63}$/.test(code) ? code : "LOOKUP_FAILED";
}

export type WalletLookupOutcome = "found" | "not_found" | "error";

export interface TransferEstimateView {
  networkFee: string;
  providerFee: string;
  totalFee: string;
  requiresApproval: boolean | null;
  approvalPolicy: string | null;
  sufficientBalance: boolean | null;
  warnings: string[];
}

interface TransferDataResult {
  sourceWallets: TransferWalletOption[];
  destWallets: TransferWalletOption[];
  assets: TransferAssetOption[];
  estimate: TransferEstimateView | null;
  estimateLoading: boolean;
  estimateError: string | null;
  walletsLoading: boolean;
  /** Set only when `GET /v1/walletkit/wallets` itself failed (401/403/5xx) — distinct from a clean empty list. */
  walletsError: string | null;
  /**
   * ALL-6339: the gateway returned a next-page token, so more wallets exist than are loaded.
   * WalletKit answers at most 100 wallets per call; the picker reads further pages one at a time,
   * only when the person asks, and never loads an unbounded list.
   */
  walletsHasMore: boolean;
  walletsLoadingMore: boolean;
  /** The reason code of a failed later page; the wallets already loaded stay as they are. */
  walletsLoadMoreError: string | null;
  loadMoreWallets: () => void;
  /**
   * The reason code and support reference of the last failed by-id lookup, keyed by the wallet id
   * that was asked for, so one lookup's answer never rewrites another's message.
   */
  walletLookupErrors: Record<string, WalletLookupFailure>;
  /**
   * ALL-6339: put one wallet in the pickers by id, whichever page it is on, with
   * `GET /v1/walletkit/wallets/{id}`. Resolves "found" when the wallet is (now) in both pickers,
   * "not_found" when the gateway does not show it to this caller, or "error" on a failed read.
   * A wallet already loaded is not read again.
   */
  findWalletById: (walletId: string) => Promise<WalletLookupOutcome>;
  assetsLoading: boolean;
  /**
   * ALL-4839: whether `assets` is a complete on-chain read ("ok"), unknown because the chain read
   * failed ("unavailable"), or not offered for this wallet's network ("unsupported"). Only "ok"
   * with an empty list means the wallet holds no assets.
   */
  assetsStatus: WalletBalanceStatus;
  assetsSupportReference: string | null;
  loadAssetsForWallet: (walletId: string) => void;
  loadEstimate: (params: { sourceWalletId: string; destination: TransferDestinationInput; asset: string; amount: string }) => void;
  submitTransfer: (draft: {
    sourceWalletId: string; destination: TransferDestinationInput;
    asset: string; amount: string;
  }) => Promise<{ ok: boolean; transferId?: string; error?: string; supportReference?: string; capability?: string }>;
}

/**
 * What the transfer API needs about a wallet, kept apart from the picker's display strings
 * (ALL-5674). The picker and the asset rows show `"<chain> <network>"` ("ethereum sepolia"),
 * which is a label, not a network id: the gateway's intent validation accepts only an
 * allow-listed network id ("sepolia") and refused every draft built from the label with
 * `destination.network: unsupported_network`. It also accepts only a 0x address as the
 * destination, so an internal destination is sent as that wallet's own on-chain address,
 * read from the tenant's wallet list, never as its wallet id.
 */
interface WalletRouting {
  network: string;
  address: string;
}

function textOrEmpty(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function walletRouting(w: Record<string, unknown>): [string, WalletRouting] {
  return [String(w.walletId ?? w.id ?? ""), { network: textOrEmpty(w.network), address: textOrEmpty(w.address) }];
}

function mapWalletToOption(w: Record<string, unknown>): TransferWalletOption {
  const id = String(w.walletId ?? w.id ?? "");
  const display = walletDisplayName({
    alias: [w.displayName, w.accountAlias, w.name].find((v) => typeof v === "string" && v.trim()) as string | undefined,
    walletId: id,
    network: typeof w.network === "string" ? w.network : null,
    chain: typeof w.chain === "string" ? w.chain : null,
    address: typeof w.address === "string" ? w.address : null,
  });
  const alias = display.name;
  const chain = String(w.chain ?? "—");
  const network = String(w.network ?? "");
  const profile = String(w.walletProfileId ?? "");
  const meta = [chain, network, profile].filter(Boolean).join(" · ");
  // ALL-5825 (UX audit D03): the picker prefers the gateway's provider label over the
  // raw route id in `meta`, searches by address, and hides the lab wallets the wallets
  // list hides. `meta` itself is untouched so the hook's own snapshots keep their
  // recorded route id. `storedAlias` is kept only when the name was derived, which is
  // exactly the verdict `isMachineNamedWallet` reads.
  const provider = textOrEmpty(w.provider) || textOrEmpty(w.custodian);
  const address = textOrEmpty(w.address);
  // A wallet the gateway reports as frozen is shown as such and cannot be chosen as a source. The
  // gateway still decides at draft creation; this only keeps the picker from offering it.
  const frozen = /^frozen$/i.test(textOrEmpty(w.status) || textOrEmpty(w.walletStatus));
  return {
    id,
    ...(frozen ? { disabled: true, frozenLabel: "Frozen" } : {}),
    name: alias || id,
    meta,
    ...(provider ? { provider } : {}),
    ...(address ? { address } : {}),
    ...(chain && chain !== "—" ? { chain } : {}),
    ...(network ? { network } : {}),
    ...(display.derived && display.alias ? { storedAlias: display.alias } : {}),
  };
}

export function useTransferData(): TransferDataResult {
  const [sourceWallets, setSourceWallets] = useState<TransferWalletOption[]>([]);
  const [destWallets, setDestWallets] = useState<TransferWalletOption[]>([]);
  const [assets, setAssets] = useState<TransferAssetOption[]>([]);
  const [estimate, setEstimate] = useState<TransferDataResult["estimate"]>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletsError, setWalletsError] = useState<string | null>(null);
  const [walletsNextToken, setWalletsNextToken] = useState<string | null>(null);
  const [walletsLoadingMore, setWalletsLoadingMore] = useState(false);
  const [walletsLoadMoreError, setWalletsLoadMoreError] = useState<string | null>(null);
  const [walletLookupErrors, setWalletLookupErrors] = useState<Record<string, WalletLookupFailure>>({});
  // Guards a double click: one later page in flight at a time, read synchronously.
  const loadingMoreRef = useRef(false);
  const unmountedRef = useRef(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsStatus, setAssetsStatus] = useState<WalletBalanceStatus>("ok");
  const [assetsSupportReference, setAssetsSupportReference] = useState<string | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  // Read by the stable submit callback, so a ref rather than state.
  const walletRoutingRef = useRef<Map<string, WalletRouting>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWalletsLoading(true);
      const result = await fetchWallets();
      if (cancelled) return;
      if (result.error) {
        setSourceWallets([]);
        setDestWallets([]);
        setWalletsNextToken(null);
        setWalletsError(result.error.reasonCode);
      } else {
        walletRoutingRef.current = new Map(result.wallets.map(walletRouting));
        const mapped = result.wallets.map(mapWalletToOption);
        setSourceWallets(mapped);
        setDestWallets(mapped);
        setWalletsNextToken(result.nextPageToken ?? null);
        setWalletsError(null);
      }
      setWalletsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  const loadMoreWallets = useCallback(() => {
    const token = walletsNextToken;
    if (!token || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setWalletsLoadingMore(true);
    setWalletsLoadMoreError(null);
    (async () => {
      const result = await fetchWallets(token).catch(
        (): WalletListResult => ({ wallets: [], error: { reasonCode: "NETWORK_ERROR" } }),
      );
      loadingMoreRef.current = false;
      if (unmountedRef.current) return;
      if (result.error) {
        // The wallets already shown stay; the token stays too, so the person can retry this page.
        setWalletsLoadMoreError(result.error.reasonCode);
      } else {
        for (const [id, routing] of result.wallets.map(walletRouting)) {
          if (!walletRoutingRef.current.has(id)) walletRoutingRef.current.set(id, routing);
        }
        const append = (current: TransferWalletOption[]) => {
          const known = new Set(current.map((w) => w.id));
          const added = result.wallets.map(mapWalletToOption).filter((w) => !known.has(w.id));
          return added.length > 0 ? [...current, ...added] : current;
        };
        setSourceWallets(append);
        setDestWallets(append);
        setWalletsNextToken(result.nextPageToken ?? null);
      }
      setWalletsLoadingMore(false);
    })();
  }, [walletsNextToken]);

  // Read synchronously by findWalletById, so a lookup never races the list it extends.
  const sourceWalletIdsRef = useRef<Set<string>>(new Set());
  const sourceWalletsRef = useRef<TransferWalletOption[]>([]);
  useEffect(() => {
    sourceWalletsRef.current = sourceWallets;
    sourceWalletIdsRef.current = new Set(sourceWallets.map((w) => w.id));
  }, [sourceWallets]);

  const findWalletById = useCallback(async (walletId: string): Promise<WalletLookupOutcome> => {
    const id = walletId.trim();
    if (!id) return "not_found";
    if (sourceWalletIdsRef.current.has(id)) {
      // A loaded wallet that is frozen cannot be a source: report it as not sendable.
      return sourceWalletsRef.current.find((w) => w.id === id)?.disabled ? "not_found" : "found";
    }
    const result: WalletLookupResult = await fetchWalletById(id).catch(
      (): WalletLookupResult => ({ wallet: null, error: { reasonCode: "NETWORK_ERROR" } }),
    );
    if (unmountedRef.current) return "error";
    // Keep the gateway's reason code and support reference, per wallet id asked for, so the person
    // and the operator see more than "failed". Only a code-shaped reason is kept.
    const failure: WalletLookupFailure | null = result.error
      ? { reasonCode: safeReasonCode(result.error.reasonCode), supportReference: result.error.supportReference ?? null }
      : null;
    setWalletLookupErrors((current) => {
      const next = { ...current };
      if (failure) next[id] = failure;
      else delete next[id];
      return next;
    });
    if (!result.wallet) {
      // One structured line per failed or refused lookup for the browser console and any RUM
      // collector: no wallet id, address or token, and a code-shaped reason only. The correlation
      // id joins it to the gateway's record of the read.
      console.warn("[wallet-lookup]", JSON.stringify({
        event: result.error ? "wallet_lookup_failed" : "wallet_lookup_not_found",
        reasonCode: failure?.reasonCode ?? null,
        supportReference: failure?.supportReference ?? null,
        correlationId: result.correlationId ?? null,
      }));
      return result.error ? "error" : "not_found";
    }
    const row = result.wallet;
    const [routingId, routing] = walletRouting(row);
    if (!walletRoutingRef.current.has(routingId)) walletRoutingRef.current.set(routingId, routing);
    const option = mapWalletToOption(row);
    // The looked-up wallet goes first, so it is on screen without scrolling past the loaded pages.
    // If a page loaded it meanwhile it is kept where it is.
    const prepend = (current: TransferWalletOption[]) =>
      current.some((w) => w.id === option.id) ? current : [option, ...current];
    setSourceWallets(prepend);
    setDestWallets(prepend);
    sourceWalletIdsRef.current.add(option.id);
    // Shown in the picker (disabled, labelled Frozen) but never reported as selectable.
    return option.disabled ? "not_found" : "found";
  }, []);

  const loadAssetsForWallet = useCallback((walletId: string) => {
    let cancelled = false;
    (async () => {
      setAssetsLoading(true);
      setAssets([]);
      setAssetsStatus("ok");
      setAssetsSupportReference(null);
      const result = await fetchWalletDetail(walletId);
      if (cancelled) return;
      const read = readWalletBalances(result);
      setAssetsStatus(read.status);
      setAssetsSupportReference(read.supportReference ?? null);
      if (result.wallet && read.status === "ok") {
        const raw = result.wallet as Record<string, unknown>;
        const wallet = (raw.wallet && typeof raw.wallet === "object" && !Array.isArray(raw.wallet))
          ? raw.wallet as Record<string, unknown>
          : raw;
        const chain = String(wallet.chain ?? "—");
        const network = String(wallet.network ?? "—");
        const profile = String(wallet.walletProfileId ?? "—");
        setAssets(read.balances.map(b => ({
          symbol: b.symbol,
          name: b.name,
          balance: b.amount,
          network: `${chain} ${network}`,
          route: profile,
        })));
      }
      setAssetsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const loadEstimate = useCallback((params: {
    sourceWalletId: string; destination: TransferDestinationInput; asset: string; amount: string;
  }) => {
    let cancelled = false;
    (async () => {
      setEstimateLoading(true);
      setEstimate(null);
      setEstimateError(null);
      const result = await fetchTransferEstimate(params);
      if (cancelled) return;
      if (result.estimate) {
        const e = result.estimate as Record<string, unknown>;
        const fee = e.estimatedFee as Record<string, unknown> | undefined;
        const breakdown = fee?.breakdown as Record<string, unknown> | undefined;
        setEstimate({
          // ALL-4937: a fee is shown only when the gateway returned a real chain estimate
          // (estimatedFee non-null); otherwise every fee cell says "Not available" and the
          // warnings carry the reason and support reference. Nothing is defaulted to a number.
          networkFee: fee && breakdown?.networkFee != null ? `${String(breakdown.networkFee)} ${String(fee.asset ?? "")}`.trim() : "Not available",
          providerFee: fee && breakdown?.platformFee != null ? String(breakdown.platformFee) : "Not available",
          totalFee: fee && fee.amount != null ? `${String(fee.amount)} ${String(fee.asset ?? "")}`.trim() : "Not available",
          requiresApproval: typeof e.requiresApproval === "boolean" ? e.requiresApproval : null,
          approvalPolicy: typeof e.approvalPolicy === "string" ? e.approvalPolicy : null,
          sufficientBalance: typeof e.sufficientBalance === "boolean" ? e.sufficientBalance : null,
          warnings: Array.isArray(e.warnings) ? e.warnings.map(String) : [],
        });
      } else {
        setEstimateError(result.error ?? "Estimate unavailable");
      }
      setEstimateLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const submitTransfer = useCallback(async (draft: {
    sourceWalletId: string; destination: TransferDestinationInput;
    asset: string; amount: string;
  }): Promise<{ ok: boolean; transferId?: string; error?: string; supportReference?: string; capability?: string }> => {
    const routing = walletRoutingRef.current;
    // The transfer moves on the source wallet's network (the asset rows come from its detail).
    const network = routing.get(draft.sourceWalletId)?.network ?? "";
    const destinationAddress = draft.destination.type === "internal"
      ? routing.get(draft.destination.walletId ?? "")?.address ?? ""
      : (draft.destination.address ?? "").trim();
    if (!network) {
      return { ok: false, error: "The source wallet has no network on record, so this transfer cannot be submitted." };
    }
    if (!destinationAddress) {
      return {
        ok: false,
        error: draft.destination.type === "internal"
          ? "The destination wallet has no on-chain address yet, so it cannot receive a transfer."
          : "Enter a destination address.",
      };
    }
    const createResult = await createTransferDraft({
      sourceWalletId: draft.sourceWalletId,
      destinationAddress,
      asset: draft.asset,
      amount: draft.amount,
      network,
    });
    if (!createResult.ok) {
      return { ok: false, error: createResult.error, supportReference: createResult.supportReference, capability: createResult.capability };
    }
    const draftId = createResult.transferId!;
    const submitResult = await submitTransferDraft(draftId);
    if (!submitResult.ok) {
      return { ok: false, transferId: draftId, error: submitResult.error, supportReference: submitResult.supportReference, capability: submitResult.capability };
    }
    // The submit is the mutation the person confirmed, so its reference is the one shown.
    // ALL-5794: the transfer is the governed intent the draft became; fall back to the draft id only
    // when the gateway did not name one.
    return { ok: true, transferId: submitResult.intentId ?? draftId, supportReference: submitResult.supportReference };
  }, []);

  return {
    sourceWallets, destWallets, assets, estimate, estimateLoading, estimateError,
    walletsLoading, walletsError,
    walletsHasMore: walletsNextToken !== null, walletsLoadingMore, walletsLoadMoreError, loadMoreWallets, walletLookupErrors, findWalletById,
    assetsLoading, assetsStatus, assetsSupportReference,
    loadAssetsForWallet, loadEstimate,
    submitTransfer,
  };
}
